import React, { useMemo, useEffect, useState } from 'react';
import { Transaction } from './Transaction/Transaction';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useSocket } from 'providers/SocketProvider';

const LOCAL_STORAGE_KEY = 'wire24_recent_txns';

const saveToLocalStorage = (txns) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(txns.slice(0, 50))); // keep only latest 50
};

const loadFromLocalStorage = () => {
  try {
    const data = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export const RecentTransactions = ({ transactions: initialTransactions }) => {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [transactions, setTransactions] = useState(
    loadFromLocalStorage() || initialTransactions || []
  );

  // Sync prop updates
  useEffect(() => {
    if (initialTransactions && initialTransactions.length > 0) {
      setTransactions(initialTransactions);
      saveToLocalStorage(initialTransactions);
    }
  }, [initialTransactions]);

  // Handle incoming transactions from socket
  useEffect(() => {
    if (!socket) return;

    const handleNewTransaction = (eventName, data) => {
      const newTxs = Array.isArray(data?.data) ? data.data : [];
      if (!newTxs.length) return;

      setTransactions((prev) => {
        const combined = [...newTxs, ...prev];
        const seen = new Set();

        const deduped = combined.filter((tx) => {
          const id = tx.id || tx.transactionId;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        saveToLocalStorage(deduped);
        return deduped;
      });
    };

    socket.onAny(handleNewTransaction);
    return () => socket.offAny(handleNewTransaction);
  }, [socket]);

  const getHoursDiff = (timestamp) => {
    const now = new Date();
    const createdAt = new Date(timestamp);
    return (now - createdAt) / (1000 * 60 * 60);
  };

  const processedTransactions = useMemo(() => {
    const grouped = [];
    const seenIds = new Set();

    transactions.forEach((txn) => {
      const { transactionType, transactionId } = txn;
      const isSwap = transactionType === 'Swap';
      const isTransfer = ['Send', 'Receive'].includes(transactionType);

      if ((isSwap || isTransfer) && transactionId) {
        if (seenIds.has(transactionId)) return;

        const matching = transactions.filter(
          (t) =>
            t.transactionId === transactionId &&
            (isSwap
              ? t.transactionType === 'Swap'
              : ['Send', 'Receive'].includes(t.transactionType))
        );

        if (matching.length > 1) {
          grouped.push({
            ...matching[0],
            ...(isSwap
              ? { groupedSwaps: matching }
              : { groupedTransfers: matching }),
          });
        } else {
          grouped.push(txn);
        }

        seenIds.add(transactionId);
      } else {
        grouped.push(txn);
      }
    });

    return grouped;
  }, [transactions]);

  return (
    <>
      {processedTransactions.length > 0 ? (
        <div className="flex flex-col space-y-2">
          <AnimatePresence initial={false}>
            {processedTransactions.map((transaction, index) => (
              <motion.div
                key={transaction.id || `${transaction.transactionId}-group`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
              >
                <Transaction
                  transaction={transaction}
                  isTop={index === 0}
                  shouldAnimate={
                    index === 0 && getHoursDiff(transaction.createdAt) <= 4
                  }
                  isGrouped={
                    Array.isArray(transaction.groupedSwaps) ||
                    Array.isArray(transaction.groupedTransfers)
                  }
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-xl font-bold text-white dark:text-white">
            Seems like it's quiet over here..
          </p>
          <p className="text-white/20 dark:text-white/70 mt-2 text-sm font-light">
            No recent transactions have been recorded for today.
          </p>
          <button
            onClick={() => navigate('/app/transactions')}
            className="mt-4 px-4 py-2 bg-gray-100/10 text-white rounded-full hover:bg-[#01bbffc5] transition"
          >
            Go to Transactions
          </button>
        </div>
      )}
    </>
  );
};
